import { Capacitor } from '@capacitor/core'
import React, {
  Children,
  cloneElement,
  isValidElement,
  PropsWithChildren,
  useLayoutEffect,
  useRef,
  useState
} from 'react'
import { Button as RACButton, TooltipTrigger } from 'react-aria-components'
import { twMerge } from 'tailwind-merge'
import { tv } from 'tailwind-variants'
import Tooltip from './Tooltip'
import { useBadgeWidth } from './useBadgeWidth'

export enum BadgeSizes {
  xs = 'xsmall',
  s = 'small',
  m = 'medium',
  l = 'large'
}

export type BadgeColors =
  | 'white'
  | 'neutral'
  | 'dark_neutral'
  | 'brand'
  | 'success'
  | 'warning'
  | 'critical'
  | 'orange'
  | 'yellow'
  | 'lime'
  | 'green'
  | 'teal'
  | 'cyan'
  | 'sky'
  | 'blue'
  | 'indigo'
  | 'violet'
  | 'purple'
  | 'fuchsia'
  | 'pink'
  | 'rose'
  | 'highlight'
  | 'highlight-dark'

export const deterministicGetRandomColorFromText = (text: string) => {
  if (!text) return 'neutral'
  const textString = String(text)
  const colors: BadgeColors[] = [
    'blue',
    'green',
    'orange',
    'purple',
    'teal',
    'yellow',
    'teal',
    'sky',
    'orange',
    'teal',
    'violet',
    'pink',
    'indigo',
    'cyan',
    'lime',
    'sky',
    'fuchsia'
  ]
  const charCodeSum = textString
    .split('')
    .reduce((acc, char) => acc + char.charCodeAt(0), 0)
  return colors[charCodeSum % colors.length]
}

export interface BadgeProps {
  size: BadgeSizes
  color?: BadgeColors
  iconLeading?: React.ReactNode
  blockTooltip?: boolean
  iconTrailing?: React.ReactNode
  specificTextForColoringCalculation?: string
  dark?: boolean
  className?: string
  useColorsFromClasses?: boolean
  containerWidth?: number | undefined
  shape?: 'rounded' | 'cut-left' | 'cut-right' | 'cut-top' | 'cut-bottom'
}

const styles = tv({
  base: 'inline-flex items-center justify-center font-medium px-2 gap-0.5 truncate ... mix-blend-normal shrink-0 overflow-hidden interpolate-size-allow-keywords transition-all duration-150',
  variants: {
    size: {
      xsmall: 'text-[11px] h-[18px]',
      small: 'text-xs h-5',
      medium: 'text-sm h-6',
      large: 'text-base h-8'
    },
    dark: {
      true: 'mix-blend-lighten'
    },
    color: {
      white: 'bg-neutral-0 text-neutral-800 mix-blend-normal',
      neutral: 'bg-neutral-100 text-neutral-700',
      dark_neutral: 'bg-slate-600 text-slate-50',
      brand: 'bg-brand-100 text-brand-700',
      success: 'bg-success-100 text-success-700',
      warning: 'bg-warning-100 text-warning-700',
      critical: 'bg-critical-100 text-critical-700',
      orange: 'bg-orange-100 text-orange-700',
      yellow: 'bg-yellow-100 text-yellow-700',
      lime: 'bg-lime-100 text-lime-700',
      green: 'bg-green-100 text-green-700',
      teal: 'bg-teal-100 text-teal-700',
      cyan: 'bg-cyan-100 text-cyan-700',
      sky: 'bg-sky-100 text-sky-700',
      blue: 'bg-blue-100 text-blue-700',
      indigo: 'bg-indigo-100 text-indigo-700',
      violet: 'bg-violet-100 text-violet-700',
      purple: 'bg-purple-100 text-purple-700',
      fuchsia: 'bg-fuchsia-100 text-fuchsia-700',
      pink: 'bg-pink-100 text-pink-700',
      highlight: 'bg-highlight-100 text-highlight-700',
      'highlight-dark': 'bg-highlight-600 text-neutral-0',
      rose: 'bg-rose-100 text-rose-700'
    },
    shape: {
      rounded: 'rounded-xl',
      'cut-left': 'rounded-lg rounded-l-none',
      'cut-right': 'rounded-lg rounded-r-none',
      'cut-top': 'rounded-lg rounded-t-none',
      'cut-bottom': 'rounded-lg rounded-b-none'
    }
  },
  defaultVariants: {
    shape: 'rounded'
  }
})

const Badge = ({
  size,
  color,
  children,
  iconLeading,
  iconTrailing,
  className,
  dark,
  useColorsFromClasses,
  containerWidth,
  blockTooltip = false,
  shape
}: PropsWithChildren<BadgeProps>) => {
  const isResponsive =
    containerWidth !== undefined && typeof children === 'string'

  const iconSize = (props: unknown) =>
    (props as { width?: number; size?: number; height?: number }).width ??
    (props as { width?: number; size?: number; height?: number }).size ??
    (props as { width?: number; size?: number; height?: number }).height ??
    16
  const { ref, badgeGreaterThanContainer } = useBadgeWidth(
    containerWidth,
    React.isValidElement(iconLeading) ? iconSize(iconLeading.props) : 0,
    React.isValidElement(iconTrailing) ? iconSize(iconTrailing.props) : 0
  )

  return (
    <div
      ref={ref}
      className={twMerge(
        styles({
          size,
          color: useColorsFromClasses
            ? undefined
            : color ??
              deterministicGetRandomColorFromText(
                (children as string | undefined) ?? ''
              ),
          dark,
          shape
        }),
        className
      )}
      style={{
        maxWidth: containerWidth ? containerWidth : 'auto'
      }}>
      {iconLeading && <span>{iconLeading}</span>}
      {isResponsive && badgeGreaterThanContainer && !blockTooltip ? (
        <TooltipTrigger delay={300}>
          <RACButton
            onPress={e => e.continuePropagation()}
            className='p-0 m-0 truncate w-full'>
            <span>{children}</span>
          </RACButton>
          <Tooltip placement={'top'} type={'dark'} hasArrow>
            <Tooltip.Title type={'dark'}>{children}</Tooltip.Title>
          </Tooltip>
        </TooltipTrigger>
      ) : (
        <span className={'truncate ...'}>{children}</span>
      )}
      {iconTrailing && <span>{iconTrailing}</span>}
    </div>
  )
}

/**
 * A responsive container component that observes its own width and passes the width
 * as a prop (`containerWidth`) to its child components. This is useful for components
 * that need to adapt their behavior or appearance based on the container's width.
 *
 * @param children - The child components to be rendered inside the container. Each child
 *                   should be a valid React element, and the `children` prop of each child
 *                   should be of type `string`.
 *
 * @example
 * <BadgeResponsiveContainer>
 *   <Badge>Example Badge</Badge>
 * </BadgeResponsiveContainer>
 */
export const BadgeResponsiveContainer: React.FC<{
  children: React.ReactElement<{ children: string }>
}> = ({ children }) => {
  const ref = useRef<HTMLDivElement>(null)
  const [width, setWidth] = useState<number | undefined>(undefined)

  useLayoutEffect(() => {
    const element = ref.current
    if (!element) return

    if (Capacitor.isNativePlatform()) {
      setWidth(window.innerWidth / 2)
      return
    }

    let frame: number
    const resizeObserver = new ResizeObserver(entries => {
      for (let entry of entries) {
        if (entry.contentRect) {
          cancelAnimationFrame(frame)
          frame = requestAnimationFrame(() => setWidth(entry.contentRect.width))
        }
      }
    })

    resizeObserver.observe(element)

    return () => {
      resizeObserver.disconnect()
      cancelAnimationFrame(frame)
    }
  }, [])

  return (
    <div ref={ref} className='w-full flex justify-start items-center'>
      {Children.map(children, child =>
        isValidElement<{ containerWidth?: number }>(child)
          ? cloneElement(child, {
              containerWidth: width
            })
          : child
      )}
    </div>
  )
}

export default Badge
